import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DatabaseError, DataIntegrityError } from "../auth/errors";

import { AccountId, ShopperAiIntegrationId } from "../ids/definitions";
import {
  EmailAlreadyConnectedToAnotherTeamError,
  EmailConnectedWithAnotherProviderError,
} from "../models/shopper-ai/integration/email-already-connected-error";
import {
  IntegrationCursor,
  IntegrationWithChannelDataResponse,
  UpdateContactFormIntegrationInput,
} from "../models/shopper-ai/integration/integration";
import {
  CursorPaginatedResponse,
  CursorPaginationParams,
} from "../schema/pagination";

/**
 * Wire-level setting entry. Permissive on purpose: a `key`/`value`
 * pair where the relationship between the two is enforced server-side
 * via the registry's `settingDecoders`. The merchant client wraps this
 * in a typed helper so callers get a strict `K → SettingValueType<K>`
 * binding at compile time.
 */
export const SetIntegrationSettingEntry = Schema.Struct({
  key: Schema.String,
  value: Schema.Unknown,
});
export type SetIntegrationSettingEntry = typeof SetIntegrationSettingEntry.Type;

export const SetIntegrationSettingsPayload = Schema.Struct({
  settings: Schema.Array(SetIntegrationSettingEntry),
});
export type SetIntegrationSettingsPayload =
  typeof SetIntegrationSettingsPayload.Type;

export const SetIntegrationSettingsResponse = Schema.Struct({
  updated: Schema.Number,
  skipped: Schema.Array(
    Schema.Struct({ key: Schema.String, reason: Schema.String }),
  ),
});
export type SetIntegrationSettingsResponse =
  typeof SetIntegrationSettingsResponse.Type;

export const UpdateEmailIntegrationNamePayload = Schema.Struct({
  name: Schema.String,
});
export type UpdateEmailIntegrationNamePayload =
  typeof UpdateEmailIntegrationNamePayload.Type;

export const ConfigureCustomEmailPayload = Schema.Struct({
  email: Schema.String,
  name: Schema.String,
});
export type ConfigureCustomEmailPayload =
  typeof ConfigureCustomEmailPayload.Type;

export const ConfigureCustomEmailResponse = Schema.Struct({
  redirectEmail: Schema.String,
  dkimHost: Schema.String,
  dkimText: Schema.String,
  returnPathDomain: Schema.String,
  returnPathValue: Schema.String,
  setupStatus: Schema.String,
});
export type ConfigureCustomEmailResponse =
  typeof ConfigureCustomEmailResponse.Type;

export const VerifyCustomEmailPayload = Schema.Struct({ email: Schema.String });
export type VerifyCustomEmailPayload = typeof VerifyCustomEmailPayload.Type;

export const VerifyCustomEmailResponse = Schema.Struct({
  dkimVerified: Schema.Boolean,
  returnPathVerified: Schema.Boolean,
});
export type VerifyCustomEmailResponse = typeof VerifyCustomEmailResponse.Type;

// DNS-record kinds/statuses mirror the gmail-common enums (wire = string values).
export const GmailDnsRecordKind = Schema.Literal("spf", "dkim", "dmarc");
export type GmailDnsRecordKind = typeof GmailDnsRecordKind.Type;

export const GmailDnsRecordStatus = Schema.Literal(
  "ok",
  "missing",
  "misconfigured",
  "error",
  "not_applicable",
);
export type GmailDnsRecordStatus = typeof GmailDnsRecordStatus.Type;

export const GmailDnsRecordCheck = Schema.Struct({
  status: GmailDnsRecordStatus,
  observed: Schema.optional(Schema.NullOr(Schema.String)),
});
export type GmailDnsRecordCheck = typeof GmailDnsRecordCheck.Type;

export const GmailDnsCheckResponse = Schema.Struct({
  domain: Schema.String,
  result: Schema.Record({
    key: GmailDnsRecordKind,
    value: GmailDnsRecordCheck,
  }),
});
export type GmailDnsCheckResponse = typeof GmailDnsCheckResponse.Type;

export const GetTeamIntegrationsParams = Schema.Struct({
  ...CursorPaginationParams(IntegrationCursor).fields,
  includeDeleted: Schema.optional(Schema.BooleanFromString),
});
export type GetTeamIntegrationsParams = typeof GetTeamIntegrationsParams.Type;

export const IntegrationGroup = HttpApiGroup.make("integration")
  .add(
    endpoint.merchant
      .get("getTeamIntegrations", "/integrations")

      .setUrlParams(GetTeamIntegrationsParams)
      .addSuccess(
        CursorPaginatedResponse(
          IntegrationWithChannelDataResponse,
          IntegrationCursor,
        ),
      )
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getIntegration", "/integrations/:id")

      .setPath(Schema.Struct({ id: ShopperAiIntegrationId }))
      .addSuccess(IntegrationWithChannelDataResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getIntegrationsByAccount", "/accounts/:accountId/integrations")

      .setPath(Schema.Struct({ accountId: AccountId }))
      .setUrlParams(CursorPaginationParams(IntegrationCursor))
      .addSuccess(
        CursorPaginatedResponse(
          IntegrationWithChannelDataResponse,
          IntegrationCursor,
        ),
      )
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("setIntegrationSettings", "/integrations/:id/settings")

      .setPath(Schema.Struct({ id: ShopperAiIntegrationId }))
      .setPayload(SetIntegrationSettingsPayload)
      .addSuccess(SetIntegrationSettingsResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateContactFormIntegration", "/integrations/contact-form")
      .setPayload(UpdateContactFormIntegrationInput)
      .addSuccess(Schema.Struct({ ok: Schema.Literal(true) }))
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteIntegration", "/integrations/:id")
      .setPath(Schema.Struct({ id: ShopperAiIntegrationId }))
      .addSuccess(Schema.Struct({ ok: Schema.Literal(true) }))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateEmailIntegrationName", "/email-integrations/:id/name")
      .setPath(Schema.Struct({ id: ShopperAiIntegrationId }))
      .setPayload(UpdateEmailIntegrationNamePayload)
      .addSuccess(Schema.Struct({ ok: Schema.Literal(true) }))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "checkGmailEmailIntegrationDns",
        "/email-integrations/gmail/:id/dns-check",
      )
      .setPath(Schema.Struct({ id: ShopperAiIntegrationId }))
      .addSuccess(GmailDnsCheckResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post(
        "configureCustomEmailIntegration",
        "/email-integrations/custom-email",
      )
      .setPayload(ConfigureCustomEmailPayload)
      .addSuccess(ConfigureCustomEmailResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(EmailAlreadyConnectedToAnotherTeamError)
      .addError(EmailConnectedWithAnotherProviderError)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post(
        "verifyCustomEmailIntegration",
        "/email-integrations/custom-email/verify",
      )
      .setPayload(VerifyCustomEmailPayload)
      .addSuccess(VerifyCustomEmailResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
